import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

import Icon from '../../../components/AppIcon';

const WelcomeSetupCard = ({ onDismiss }) => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [tasks, setTasks] = useState([
    {
      id: 'profile',
      label: 'Complete your profile',
      description: 'Add your photo, phone number, and job title',
      icon: 'UserCircle',
      path: '/profile-setup',
      completed: false,
    },
    {
      id: 'team',
      label: 'View your team',
      description: 'See who else is on your team',
      icon: 'Users',
      path: '/team-administration',
      completed: false,
    },
    {
      id: 'project',
      label: 'Explore your first project',
      description: 'Browse active projects and get started',
      icon: 'Briefcase',
      path: '/project-management',
      completed: false,
    },
  ]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check localStorage for dismissed state
    const key = `welcome_dismissed_${userProfile?.id}`;
    if (localStorage.getItem(key) === 'true') {
      setDismissed(true);
      return;
    }

    // Check profile completion
    if (userProfile) {
      setTasks(prev => prev?.map(t => {
        if (t?.id === 'profile') {
          return { ...t, completed: !!(userProfile?.phone && userProfile?.title && userProfile?.avatar_url) };
        }
        return t;
      }));
    }

    // Check if user has viewed team page
    const teamViewed = localStorage.getItem(`team_viewed_${userProfile?.id}`) === 'true';
    const projectViewed = localStorage.getItem(`project_viewed_${userProfile?.id}`) === 'true';
    setTasks(prev => prev?.map(t => {
      if (t?.id === 'team') return { ...t, completed: teamViewed };
      if (t?.id === 'project') return { ...t, completed: projectViewed };
      return t;
    }));
  }, [userProfile]);

  const allCompleted = tasks?.every(t => t?.completed);
  const completedCount = tasks?.filter(t => t?.completed)?.length;

  const handleDismiss = () => {
    const key = `welcome_dismissed_${userProfile?.id}`;
    localStorage.setItem(key, 'true');
    setDismissed(true);
    onDismiss?.();
  };

  const handleTaskClick = (task) => {
    if (task?.id === 'team') {
      localStorage.setItem(`team_viewed_${userProfile?.id}`, 'true');
      setTasks(prev => prev?.map(t => t?.id === 'team' ? { ...t, completed: true } : t));
    }
    if (task?.id === 'project') {
      localStorage.setItem(`project_viewed_${userProfile?.id}`, 'true');
      setTasks(prev => prev?.map(t => t?.id === 'project' ? { ...t, completed: true } : t));
    }
    navigate(task?.path);
  };

  if (dismissed) return null;

  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5 md:p-6 relative">
      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="absolute top-4 right-4 text-amber-400 hover:text-amber-600 transition-colors"
        title="Dismiss"
      >
        <Icon name="X" size={18} />
      </button>
      {/* Header */}
      <div className="flex items-start gap-4 mb-5">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-sm">
          <Icon name="Sparkles" size={22} color="#ffffff" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">
            Welcome{userProfile?.full_name ? `, ${userProfile?.full_name?.split(' ')?.[0]}` : ''}! 🎉
          </h3>
          <p className="text-sm text-gray-600 mt-0.5">
            Get started by completing these quick setup steps.
          </p>
        </div>
      </div>
      {/* Progress bar */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-medium text-amber-700">{completedCount} of {tasks?.length} completed</span>
          {allCompleted && (
            <span className="text-xs font-semibold text-green-600 flex items-center gap-1">
              <Icon name="CheckCircle" size={12} />
              All done!
            </span>
          )}
        </div>
        <div className="h-2 bg-amber-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all duration-500"
            style={{ width: `${(completedCount / tasks?.length) * 100}%` }}
          />
        </div>
      </div>
      {/* Task list */}
      <div className="space-y-2.5">
        {tasks?.map((task) => (
          <button
            key={task?.id}
            onClick={() => handleTaskClick(task)}
            className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
              task?.completed
                ? 'bg-green-50 border-green-200 opacity-75' :'bg-white border-amber-200 hover:border-amber-400 hover:shadow-sm'
            }`}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
              task?.completed ? 'bg-green-100' : 'bg-amber-100'
            }`}>
              {task?.completed ? (
                <Icon name="Check" size={16} className="text-green-600" />
              ) : (
                <Icon name={task?.icon} size={16} className="text-amber-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${
                task?.completed ? 'text-gray-400 line-through' : 'text-gray-800'
              }`}>
                {task?.label}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{task?.description}</p>
            </div>
            {!task?.completed && (
              <Icon name="ChevronRight" size={16} className="text-amber-400 flex-shrink-0" />
            )}
          </button>
        ))}
      </div>
      {/* Dismiss when all done */}
      {allCompleted && (
        <button
          onClick={handleDismiss}
          className="mt-4 w-full py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all shadow-sm"
        >
          Got it, dismiss this card
        </button>
      )}
    </div>
  );
};

export default WelcomeSetupCard;
